export const LOBBY_AMBIENT_ANIMATION_ALIASES = [
  'emoteGG',
  'emoteOof',
  'emoteTaunt',
  'emoteThanks',
  'emoteCry',
  'emoteCheer',
] as const;

export type LobbyAmbientAnimationAlias = (typeof LOBBY_AMBIENT_ANIMATION_ALIASES)[number];

export interface LobbyAmbientAnimationClip {
  readonly alias: string;
  readonly durationMs: number;
}

export interface LobbyAmbientAnimationState {
  readonly alias: string | null;
  readonly nextChangeMs: number;
  readonly remainingAliases: readonly string[];
}

export interface LobbyAmbientAnimationSchedulerOptions {
  readonly aliases?: readonly string[];
  readonly random?: () => number;
}

export interface LobbyAmbientAnimationScheduler {
  getAnimationAlias(
    playerId: string,
    nowMs: number,
    availableClips: readonly LobbyAmbientAnimationClip[],
  ): string;
  clearPlayer(playerId: string): void;
  clear(): void;
}

export function createLobbyAmbientAnimationScheduler(
  options: LobbyAmbientAnimationSchedulerOptions = {},
): LobbyAmbientAnimationScheduler {
  const allowedAliases = [...(options.aliases ?? LOBBY_AMBIENT_ANIMATION_ALIASES)];
  const allowedAliasSet = new Set(allowedAliases);
  const random = options.random ?? Math.random;
  const states = new Map<string, LobbyAmbientAnimationState>();

  function normalizeAvailableClips(
    availableClips: readonly LobbyAmbientAnimationClip[],
  ): LobbyAmbientAnimationClip[] {
    const byAlias = new Map<string, LobbyAmbientAnimationClip>();
    for (const clip of availableClips) {
      if (!allowedAliasSet.has(clip.alias)) continue;
      if (!Number.isFinite(clip.durationMs) || clip.durationMs <= 0) continue;
      if (byAlias.has(clip.alias)) continue;
      byAlias.set(clip.alias, clip);
    }
    return allowedAliases.flatMap((alias) => {
      const clip = byAlias.get(alias);
      return clip == null ? [] : [clip];
    });
  }

  function pickRandomAlias(aliases: readonly string[]): string {
    return aliases[Math.floor(Math.max(0, Math.min(0.999999, random())) * aliases.length)]!;
  }

  function getNextBag(args: {
    readonly remainingAliases: readonly string[];
    readonly availableAliases: readonly string[];
  }): string[] {
    const available = new Set(args.availableAliases);
    const filteredRemaining = args.remainingAliases.filter((alias) => available.has(alias));
    if (filteredRemaining.length > 0) return filteredRemaining;
    return [...args.availableAliases];
  }

  function getPickableAliases(args: {
    readonly currentAlias: string | null;
    readonly bag: readonly string[];
  }): readonly string[] {
    if (args.currentAlias == null || args.bag.length <= 1) return args.bag;
    const withoutCurrent = args.bag.filter((alias) => alias !== args.currentAlias);
    return withoutCurrent.length > 0 ? withoutCurrent : args.bag;
  }

  return {
    getAnimationAlias(
      playerId: string,
      nowMs: number,
      availableClips: readonly LobbyAmbientAnimationClip[],
    ): string {
      const normalizedClips = normalizeAvailableClips(availableClips);
      if (normalizedClips.length === 0) {
        states.delete(playerId);
        return 'idle';
      }

      const current = states.get(playerId);
      if (
        current != null &&
        current.alias != null &&
        current.nextChangeMs > nowMs &&
        normalizedClips.some((clip) => clip.alias === current.alias)
      ) {
        return current.alias;
      }

      const availableAliases = normalizedClips.map((clip) => clip.alias);
      const bag = getNextBag({
        remainingAliases: current?.remainingAliases ?? [],
        availableAliases,
      });
      const alias = pickRandomAlias(
        getPickableAliases({
          currentAlias: current?.alias ?? null,
          bag,
        }),
      );
      const clip = normalizedClips.find((candidate) => candidate.alias === alias)!;

      states.set(playerId, {
        alias,
        nextChangeMs: nowMs + clip.durationMs,
        remainingAliases: bag.filter((candidate) => candidate !== alias),
      });
      return alias;
    },

    clearPlayer(playerId: string): void {
      states.delete(playerId);
    },

    clear(): void {
      states.clear();
    },
  };
}
